import React, { useState, useRef, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://provider-portal-api-production.gustavdatadc.workers.dev';

// Simple animated spinner
const Spinner = ({ size = 20 }) => (
  <div style={{
    width: size,
    height: size,
    border: '2px solid rgba(255,255,255,0.3)',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'quickUploadSpin 0.8s linear infinite',
  }} />
);

/**
 * QuickUpload Component - Fire and Forget Upload
 * 
 * Provider uploads PDFs → Immediate success message → Background processing
 * No waiting, no extraction preview, no clicking Process
 */
const QuickUpload = ({ apiToken, onUploadComplete }) => {
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length > 0) {
      uploadFiles(Array.from(e.dataTransfer.files));
    }
  }, []);

  const handleFileSelect = useCallback((e) => {
    if (e.target.files?.length > 0) {
      uploadFiles(Array.from(e.target.files));
    }
  }, []);

  const uploadFiles = async (files) => {
    setError(null);
    setUploadResult(null);
    setIsUploading(true);

    // Validate files
    const validFiles = files.filter(file => {
      const isPdf = file.name.toLowerCase().endsWith('.pdf');
      const isSizeOk = file.size <= 20 * 1024 * 1024; // 20MB max
      return isPdf && isSizeOk;
    });

    if (validFiles.length === 0) {
      setError('Please upload PDF files only (max 20MB each)');
      setIsUploading(false);
      return;
    }

    if (validFiles.length !== files.length) {
      setError(`${files.length - validFiles.length} file(s) skipped (only PDFs allowed, max 20MB)`);
    }

    try {
      const formData = new FormData();
      validFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('priority', 'P3');

      console.log('[QUICK-UPLOAD] Sending', validFiles.length, 'files');

      const response = await fetch(`${API_BASE_URL}/api/cases/quick-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiToken}`
        },
        body: formData
      });

      // Check content type
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('[QUICK-UPLOAD] Non-JSON response:', text.substring(0, 500));
        throw new Error('Server returned invalid response. Please try again.');
      }

      if (!response.ok) {
        throw new Error(data.error || data.details || `Upload failed (${response.status})`);
      }

      console.log('[QUICK-UPLOAD] Success:', data);
      setUploadResult(data);
      
      // Notify parent
      onUploadComplete?.(data);
      
      // Auto-clear after 10 seconds
      setTimeout(() => {
        setUploadResult(null);
        setError(null);
      }, 10000);

    } catch (err) {
      console.error('[QUICK-UPLOAD] Error:', err);
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClear = () => {
    setUploadResult(null);
    setError(null);
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>📤</div>
        <h2 style={{ margin: '0 0 8px 0', fontSize: 24, fontWeight: 600 }}>Quick Upload</h2>
        <p style={{ margin: 0, color: '#666', fontSize: 15 }}>
          Upload documents and we'll handle the rest
        </p>
      </div>

      {/* Upload Zone */}
      {!isUploading && !uploadResult && (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `3px dashed ${dragActive ? '#4caf50' : '#bdbdbd'}`,
            borderRadius: 16,
            padding: '50px 32px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragActive ? '#e8f5e9' : '#fafafa',
            transition: 'all 0.2s ease',
            boxShadow: dragActive ? '0 4px 20px rgba(76, 175, 80, 0.2)' : 'none',
          }}
        >
          <div style={{ fontSize: 64, marginBottom: 16 }}>
            {dragActive ? '📂' : '📁'}
          </div>
          <p style={{ margin: '0 0 8px 0', fontSize: 18, fontWeight: 500, color: '#333' }}>
            {dragActive ? 'Drop files here!' : 'Drag & drop PDF files'}
          </p>
          <p style={{ margin: 0, fontSize: 14, color: '#888' }}>
            or click to browse • Multiple files allowed
          </p>
          <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#aaa' }}>
            PDF files only • Max 20MB each
          </p>
          <input 
            ref={fileInputRef} 
            type="file" 
            accept=".pdf" 
            onChange={handleFileSelect} 
            style={{ display: 'none' }} 
            multiple
          />
        </div>
      )}

      {/* Uploading State */}
      {isUploading && (
        <div style={{
          border: '3px solid #e0e0e0',
          borderRadius: 16,
          padding: '60px 32px',
          textAlign: 'center',
          background: '#fafafa',
        }}>
          <div style={{ marginBottom: 20 }}>
            <Spinner size={48} />
          </div>
          <p style={{ margin: '0 0 8px 0', fontSize: 18, fontWeight: 500, color: '#333' }}>
            Uploading...
          </p>
          <p style={{ margin: 0, fontSize: 14, color: '#888' }}>
            Sending files to our secure AI processing pipeline
          </p>
        </div>
      )}

      {/* Success State */}
      {uploadResult && (
        <div style={{
          border: '3px solid #4caf50',
          borderRadius: 16,
          padding: '32px',
          background: '#f1f8e9',
        }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: 22, color: '#2e7d32' }}>
              Upload Complete!
            </h3>
            <p style={{ margin: 0, fontSize: 16, color: '#558b2f' }}>
              {uploadResult.message}
            </p>
          </div>

          {/* Case Summary */}
          {uploadResult.summary?.cases?.length > 0 && (
            <div style={{
              background: 'white',
              borderRadius: 12,
              padding: '16px 20px',
              marginBottom: 16,
            }}>
              <p style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 600, color: '#333' }}>
                Cases Created:
              </p>
              {uploadResult.summary.cases.map((c, i) => (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '8px 0',
                  borderBottom: i < uploadResult.summary.cases.length - 1 ? '1px solid #eee' : 'none',
                  fontSize: 13,
                }}>
                  <span style={{ color: '#666' }}>{c.caseId}</span>
                  <span style={{ 
                    padding: '2px 8px', 
                    background: '#e3f2fd', 
                    color: '#1565c0',
                    borderRadius: 10,
                    fontSize: 11,
                    fontWeight: 500,
                  }}>
                    {c.status}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Background Processing Note */}
          <div style={{
            background: '#fff3e0',
            borderRadius: 10,
            padding: '14px 16px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
          }}>
            <span style={{ fontSize: 20 }}>⚡</span>
            <div>
              <p style={{ margin: '0 0 4px 0', fontSize: 14, fontWeight: 500, color: '#e65100' }}>
                Processing in Background
              </p>
              <p style={{ margin: 0, fontSize: 13, color: '#666', lineHeight: 1.5 }}>
                Our AI is extracting data from your documents. Check the <strong>Cases</strong> tab in a few minutes to see the results.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={handleClear}
              style={{
                padding: '12px 24px',
                borderRadius: 8,
                border: '2px solid #4caf50',
                background: 'white',
                color: '#4caf50',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Upload More
            </button>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !uploadResult && (
        <div style={{
          border: '3px solid #d32f2f',
          borderRadius: 16,
          padding: '24px 32px',
          background: '#ffebee',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>❌</div>
          <h3 style={{ margin: '0 0 8px 0', fontSize: 18, color: '#c62828' }}>
            Upload Failed
          </h3>
          <p style={{ margin: '0 0 16px 0', fontSize: 15, color: '#666' }}>
            {error}
          </p>
          <button
            onClick={handleClear}
            style={{
              padding: '10px 20px',
              borderRadius: 8,
              border: 'none',
              background: '#d32f2f',
              color: 'white',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Info Card */}
      <div style={{
        marginTop: 24,
        padding: '18px 20px',
        background: '#f8f9fa',
        borderRadius: 12,
        borderLeft: '4px solid #1976d2',
      }}>
        <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6 }}>
          <strong style={{ color: '#1976d2' }}>💡 What happens next?</strong>
          <ol style={{ margin: '10px 0 0 18px', padding: 0, color: '#666' }}>
            <li style={{ marginBottom: 6 }}><strong>Upload instantly</strong> — Files sent to secure storage</li>
            <li style={{ marginBottom: 6 }}><strong>AI extracts data</strong> — Patient info, CPT/ICD codes, etc.</li>
            <li style={{ marginBottom: 6 }}><strong>We create cases</strong> — One per document</li>
            <li style={{ marginBottom: 6 }}><strong>You review later</strong> — Check Cases tab for results</li>
          </ol>
          <p style={{ margin: '10px 0 0 0', fontSize: 12, color: '#888', fontStyle: 'italic' }}>
            Average processing time: 1-3 minutes per document
          </p>
        </div>
      </div>

      <style>{`
        @keyframes quickUploadSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default QuickUpload;
